import React, { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Container, Row, Col, UncontrolledTooltip } from "reactstrap";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import "toastr/build/toastr.min.css";
import DateUtils from "../../../../../services/utils/DateUtils";
import axios from "axios";
import {
    API_URL_MY_TASKS
} from '../../../constants';
import {
    INTERNATIONAL_DATE_FORMAT,
} from "../../../../../common/constants";
import { withRouter } from "react-router-dom";
import { EMPTY_LIST } from "../../../../../common/constants";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import Navigator from "../../../components/navigator/index";
import { HiChevronDoubleDown, HiChevronDoubleUp, HiMenuAlt4 } from 'react-icons/hi';
import { formatTaskAndRiskId } from "src/modules/data-protection/constants/Common";
import FilteringComponent from "./components/filtering-component";

const EmployeeMyTasks = (props) => {
    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        status: null,
        priority: null
    });

    const dateUtils = new DateUtils();

    const { t } = props;

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: false,
            key: 0,
            formatter: (cellContent, row) => {
                return (
                    <Link
                        to={`/employee/data-protection/tasks/${row.id}`}
                        className={`dt-task-title ${row.deleted_at ? '' : 'hoverable'}`}
                        style={{
                            cursor: !row.deleted_at ? 'pointer' : 'default',
                        }}
                    >
                        {formatTaskAndRiskId('task', row?.questionnaire === "software" ? row?.asset_id : row?.case_secondary_id, row?.risk_secondary_id ?? row.secondary_id, row?.is_mitigation, row?.questionnaire, row.secondary_id)}
                    </Link>
                );
            },
            headerStyle: {
                width: "220px"
            },
            style: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                verticalAlign: "middle",
            }
        },
        {
            dataField: "name",
            text: t("Title"),
            sort: false,
            style: { display: "table-cell", maxWidth: "200px" },
            headerStyle: { maxWidth: "200px" },
            key: 1,
            formatter: (cellContent, {
                id,
                name,
                is_recurring,
                recurrence_secondary_id,
                deleted_at
            }) => {
                return (
                    <div className="d-flex align-items-center">
                        {is_recurring && recurrence_secondary_id && (
                            <>
                                <i
                                    className="ri-repeat-2-line text-info me-1 font-size-18 align-middle"
                                    id={`recurring-task-${id}-tooltip`}
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                />
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`recurring-task-${id}-tooltip`}
                                >
                                    {t('Recurring task')}
                                </UncontrolledTooltip>
                            </>
                        )}

                        <Link
                            to={`/employee/data-protection/tasks/${id}`}
                            className={`dt-task-title ${deleted_at ? '' : 'hoverable'}`}
                            style={{
                                cursor: !deleted_at ? 'pointer' : 'default',
                            }}
                        >
                            {name}{recurrence_secondary_id ? `-${recurrence_secondary_id}` : ''}
                        </Link>
                    </div>
                )
            }
        },
        {
            dataField: "created_at",
            text: t("Creation Date"),
            sort: false,
            key: 2,
            style: {
                verticalAlign: "middle"
            },
            formatter: (cellContent, row) => {
                return (
                    <div className='dt-task-creation-date'>
                        {dateUtils.convertTimeStampToDate(row.created_at, INTERNATIONAL_DATE_FORMAT)}
                    </div>
                )
            },
        },
        {
            dataField: "ended_at",
            text: t("Deadline	"),
            sort: false,
            key: 3,
            style: {
                verticalAlign: "middle"
            },
            formatter: (cellContent, row) => {
                return (
                    <div className='dt-task-deadline-date'>
                        {dateUtils.convertTimeStampToDate(row.ended_at, INTERNATIONAL_DATE_FORMAT)}
                    </div>
                )
            },
        },
        {
            dataField: "priority",
            text: t("Priority"),
            sort: false,
            key: 4,
            style: {
                verticalAlign: "middle"
            },
            formatter: (cellContent, row) => {
                return (
                    <div className='dt-task-priority'>
                        {row.priority === 'high'.toLowerCase() ? (
                            <HiChevronDoubleUp className='text-danger' />
                        ) : row.priority.toLowerCase() === 'medium' ? (
                            <HiMenuAlt4 className='text-success' />
                        ) : (
                            <HiChevronDoubleDown className='text-info' />
                        )}
                        <span style={{ marginLeft: '4px' }}>{t(row.priority)}</span>
                    </div>
                );
            }
        },
        {
            dataField: "status",
            text: t("Status"),
            sort: false,
            key: 5,
            style: {
                verticalAlign: "middle"
            },
            formatter: (cellContent, row) => {
                return (
                    <div
                        className={
                            'dt-task-status ' +
                            (row.status.toLowerCase() === 'review'
                                ? 'bg-info'
                                : row.status.toLowerCase() === 'new'
                                    ? 'bg-success'
                                    : row.status.toLowerCase() === 'in_progress'
                                        ? 'bg-primary'
                                        : row.status.toLowerCase() === 'deleted'
                                            ? 'bg-danger'
                                            : 'bg-secondary')
                        }
                    >
                        {t(row.status.replace('_', ' '))}
                    </div>
                );
            }
        }
    ];

    const handleFetchMyTasksListQuery = useQuery({
        queryKey: ["data-protection-fetch-my-tasks-query", tableFilters],
        queryFn: async () => {
            const response = await axios.get(API_URL_MY_TASKS, {
                headers: {
                    Authorization: `Bearer ${props.token}`,
                },
                params: tableFilters
            });
            return response.data?.data || [];
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(props.t("An error occurred while fetching tasks."), {
                type: "error"
            });
        }
    });

    const NoDataIndication = () =>
        handleFetchMyTasksListQuery.isFetched &&
            !handleFetchMyTasksListQuery.length ? (
            <div className="alert m-0" role="alert">
                <p
                    style={{
                        textAlign: "center",
                        marginBottom: 0,
                    }}
                >
                    {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleTableChange = (type, { page, sizePerPage }) => {
        switch (type) {
            case "pagination":
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;
            default:
                return false;
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        setTableFilters((currentFilters) => ({...currentFilters, ...newFilters}));
    }, []);

    return (
        <div className="page-content">
            <Container fluid style={{ minHeight: "400px" }}>
                <Row>
                    <Col lg="12" className="mb-2">
                        <Navigator
                            breadCrumbs={[
                                { title: "SIP", link: "/" },
                                {
                                    title: "My Tasks",
                                    link: `#`,
                                }
                            ]}
                        />
                    </Col>

                    <Col lg="12">
                        <div
                            className="bg-white"
                            style={{
                                borderRadius: "7px",
                                boxShadow: "0px 0px 4px 0px #4F666E40",
                            }}
                        >
                            <div className="p-4" style={{ marginBottom: "300px" }}>
                                <PaginationProvider
                                    pagination={paginationFactory({
                                        custom: true,
                                        page: tableFilters.pageIndex,
                                        sizePerPage: tableFilters.pageSize,
                                        totalSize: handleFetchMyTasksListQuery.data?.count || 0,
                                        withFirstAndLast: false,
                                        alwaysShowAllBtns: true,
                                        prePageText: (
                                            <span>
                                                <i className="ri-arrow-left-s-line"></i>{" "}
                                                {props.t("Back")}
                                            </span>
                                        ),
                                        nextPageText: (
                                            <span>
                                                {props.t("Next")}{" "}
                                                <i className="ri-arrow-right-s-line"></i>
                                            </span>
                                        ),
                                        prePageTitle: props.t("Pre page"),
                                        firstPageTitle: props.t("Next page"),
                                        showTotal: true,
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            {
                                                text: "25",
                                                value: 25,
                                            },
                                            {
                                                text: "50",
                                                value: 50,
                                            },
                                        ],
                                    })}
                                >
                                    {({ paginationProps, paginationTableProps }) => (
                                        <React.Fragment>
                                            <Row className="mb-5 mt-3">
                                                <Col sm="12">
                                                    <div className="d-flex gap-4 justify-content-end">
                                                        <FilteringComponent 
                                                            onChange={handleFilterChange}
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col sm="12">
                                                    <BootstrapTable
                                                        remote={{
                                                            pagination: true,
                                                            filter: false,
                                                            sort: true,
                                                            cellEdit: false,
                                                            search: false,
                                                        }}
                                                        loading={
                                                            handleFetchMyTasksListQuery.isFetching
                                                        }
                                                        overlay={overlayFactory({
                                                            spinner: (
                                                                <Spinner
                                                                    animation="border"
                                                                    variant="primary"
                                                                    size="md"
                                                                />
                                                            ),
                                                            text: "Loading...",
                                                        })}
                                                        onTableChange={handleTableChange}
                                                        defaultSorted={[]}
                                                        keyField={"id"}
                                                        responsive
                                                        bordered={false}
                                                        data={handleFetchMyTasksListQuery?.data?.data || []}
                                                        striped={true}
                                                        columns={tableColumns}
                                                        wrapperClasses="table-responsive"
                                                        classes={"table tpdd-table incident-table tp-reports-table"}
                                                        headerWrapperClasses={"thead-light"}
                                                        style={{
                                                            overflowX: "auto",
                                                        }}
                                                        noDataIndication={() => <NoDataIndication />}
                                                        {...paginationTableProps}
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col sm="12" md="6">
                                                    <div className="tpdd-pagination-style-1">
                                                        <PaginationListStandalone {...paginationProps} />

                                                        <SizePerPageDropdownStandalone
                                                            {...paginationProps}
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>
                                        </React.Fragment>
                                    )}
                                </PaginationProvider>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { user: currentUser, token } = state.Login;
    const { App } = state;
    return { token, currentUser, App };
};


export default withNamespaces()(
    withRouter(connect(mapStatetoProps, null)(EmployeeMyTasks))
);
